<template>
    <div
        v-if="!$wait.is('loading-page')"
        class="m-api-market"
    >
        <div class="m-api-market__hero">
            <y-container>
                <y-hero
                    v-if="hero"
                    :hero-data="hero"
                />
            </y-container>
        </div>
        <div
            v-if="whyChooseQuarkino"
            class="m-api-market__why-quarkino"
        >
            <y-container>
                <y-choose-quarkino :content="whyChooseQuarkino" />
            </y-container>
        </div>

        <div class="m-api-market__key-features">
            <y-container>
                <y-benefit
                    :content="keyFeatures"
                    has-title-and-subtitle
                />
            </y-container>
        </div>
        <div class="m-api-market__video">
            <y-container>
                <y-pwa-video
                    v-if="videoData"
                    :streaming-data="videoData"
                    dont-show-content
                />
            </y-container>
        </div>

        <div class="m-api-market__fellow-developers">
            <y-container>
                <y-fellow-developers :content="fellowDevelopers" />
            </y-container>
        </div>

        <y-container class="m-api-market__cta-complex">
            <y-cta-complex
                v-if="complexCtaData"
                :title="complexCtaData.title"
                :solid-btn-title="complexCtaData.button_title"
                :solid-btn-link="complexCtaData.button_link"
                :image-src="complexCtaData.header_image"
                has-image-in-mobile
                disable-cta-btn
                title-tag="p"
            />
        </y-container>
        <div class="m-api-market__academy">
            <y-container>
                <y-hero
                    :hero-data="benefits"
                    title-tag="h2"
                    disable-cta-btn
                    show-coming-soon-label
                />
            </y-container>
        </div>
        <div class="m-api-market__access-guid">
            <y-container>
                <y-significance
                    v-if="significanceData"
                    :content="significanceData"
                    has-image-in-mobile
                    disable-cta-btn
                />
            </y-container>
        </div>

        <div class="m-api-market__call-to-action">
            <y-container>
                <y-call-to-action
                    v-if="callToAction"
                    :content="callToAction"
                />
            </y-container>
        </div>
    </div>
</template>

<script>

    // Mixins
    import PageMixin from '@groot-nodes/mixins/Page.js';

    // Helpers
    import { removeSymbol, formatStats, isLink } from '@/helpers/helpers';

    // Components
    import YHero from '@/components/ForPagesComponents/Hero/Hero.vue';
    import YPwaVideo from '@/modules/pwa/components/PwaVideo/Index';
    import YCallToAction from '@/modules/pwa/components/CallToAction/Index';
    import YBenefit from '@modules/why-quarkino/components/Benefit/Benefit.vue';
    import YSignificance from '@modules/content-pages/components/Significance/Significance.vue';
    import YCtaComplex from '@components/ForPagesComponents/CtaComplex/CtaComplex.vue';
    import YFellowDevelopers from '@/modules/api-market/components/FellowDevelopers/FellowDevelopers.vue';
    import YChooseQuarkino from '@/modules/api-market/components/why-quarkino/WhyQuarkino.vue';

    export default {
        name: 'ApiMarketPage',

        components: {
            YHero,
            YCallToAction,
            YPwaVideo,
            YBenefit,
            YSignificance,
            YCtaComplex,
            YFellowDevelopers,
            YChooseQuarkino,

        },

        mixins: [PageMixin],

        /**
         * @inheritdoc
         */
        data() {
            return {
                hero             : {},
                videoData        : {},
                testimonials     : {},
                keyFeatures      : null,
                whyChooseQuarkino: null,
                callToAction     : null,
                significanceData : null,
                benefits         : null,
                complexCtaData   : null,
                fellowDevelopers : null,

            };
        },

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.hero.seo_post_title,

                meta: [
                    {
                        vmid   : 'description',
                        name   : 'description',
                        content: this.hero.seo_meta_description,
                    },
                ],
            };
        },

        /**
         *
         * @inheritDoc
         */
        methods: {
            removeSymbol,
            formatStats,
            isLink,

            /**
             * Fetches scalable hosting solutions page data
             *
             * @returns {Promise} Promise object assigns the components data when resolved
             */
            fetch() {
                // Fetches the hero and features sections data and the case study title
                const heroFeaturesCaseStudy = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'api-market-hero',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'albums', 'title2', 'header_image', 'seo_canonical', 'seo_meta_description', 'seo_post_title'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // advantages data and the video
                const advantagesAndVideo = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'api-market-video',
                    including: ['title', 'image', 'custom', 'albums', 'header-image'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const testimonials = this.$services.Content.postList({
                    type     : 'testimonials',
                    tagging  : 'api-market',
                    including: ['title', 'title2', 'image', 'synopsis', 'link', 'custom'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });
                const callToAction = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'api-market-cta',
                    including: ['id', 'title', 'image',
                                'header_image', 'synopsis', 'text',
                                'custom', 'seo_meta_description',
                                'seo_post_title'],
                    locale: this.$i18n.locale,

                });

                return Promise.all(
                    [heroFeaturesCaseStudy,
                     advantagesAndVideo,
                     testimonials,
                     callToAction],
                ).then((responses) => {
                    // heroFeaturesCaseStudy
                    if (this.handleReq(responses, 0)) {
                        this.hero = {
                            title               : responses[0].data.results.title,
                            synopsis            : responses[0].data.results.synopsis,
                            image               : responses[0].data.results.image,
                            seo_canonical       : responses[0].data.results.seo_canonical,
                            seo_meta_description: responses[0].data.results.seo_meta_description,
                            seo_post_title      : responses[0].data.results.seo_post_title,
                            custom              : {
                                'button-link' : responses[0].data.results.custom['button-link'],
                                'button-title': responses[0].data.results.custom['button-title'],
                            },
                        };

                        this.discoverFeatures = {
                            title : responses[0].data.results.custom.field1,
                            albums: responses[0].data.results.albums,
                            custom: {
                                'button-title': responses[0].data.results.custom.field2,
                                'button-link' : responses[0].data.results.custom.field3,
                            },
                        };

                        this.caseStudies = {
                            ...this.caseStudies,
                            title   : responses[0].data.results.custom.field4,
                            synopsis: responses[0].data.results.custom.field5,
                            custom  : {
                                'button-title': responses[0].data.results.custom.field6,
                                'button-link' : responses[0].data.results.custom.field7,
                            },
                        };
                        this.keyFeatures = {
                            title2: responses[0].data.results.custom.field4,
                            albums: responses[0].data.results.albums.slice(3),
                            custom: {
                                ...responses[0].data.results.custom,
                                'button-link' : responses[0].data.results.custom.field6,
                                'button-title': responses[0].data.results.custom.field5,
                            },
                            image: responses[0].data.results.header_image,
                        };

                        this.whyChooseQuarkino = {
                            title       : responses[0].data.results.custom.field1,
                            albums      : responses[0].data.results.albums.slice(0, 3),
                            button_title: responses[0].data.results.custom.field2,
                            button_link : responses[0].data.results.custom.field3,

                        };
                    }
                    // advantagesAndVideo
                    if (this.handleReq(responses, 1)) {
                        this.videoData = {
                            title : responses[1].data.results.title,
                            albums: responses[1].data.results.albums.filter((item) => item.group === 'video'),
                            image : responses[1].data.results.image,
                            custom: {
                                'button-title': responses[1].data.results.custom['button-title'],
                                'button-link' : responses[1].data.results.custom['button-link'],
                                embed_link    : responses[1].data.results.custom.field7,
                            },
                        };

                        this.complexCtaData = {
                            title       : responses[1].data.results.custom.field4,
                            button_title: responses[1].data.results.custom.field5,
                            button_link : responses[1].data.results.custom.field6,
                            header_image: responses[1].data.results['header-image'],

                        };

                        this.fellowDevelopers = {
                            title       : responses[1].data.results.custom.field1,
                            button_title: responses[1].data.results.custom.field2,
                            button_link : responses[1].data.results.custom.field3,

                        };
                    }
                    // testimonials
                    if (this.handleReq(responses, 2)) {
                        this.testimonials = {
                            ...this.testimonials,
                            results: responses[2].data.results,
                        };
                        this.fellowDevelopers = { ...this.fellowDevelopers, albums: responses[2].data.results };
                    }
                    if (this.handleReq(responses, 3)) {
                        this.callToAction = {
                            title : responses[3].data.results.custom.field4,
                            custom: {
                                'button-link' : responses[3].data.results.custom.field6,
                                'button-title': responses[3].data.results.custom.field5,
                            },
                        };
                        this.significanceData = {
                            title : responses[3].data.results.custom.field1,
                            custom: {
                                'button-title': responses[3].data.results.custom.field2,
                                'button-link' : responses[3].data.results.custom.field3,
                            },
                            text : responses[3].data.results.text,
                            image: responses[3].data.results.header_image,
                        };
                        this.benefits = {
                            title   : responses[3].data.results.title,
                            synopsis: responses[3].data.results.synopsis,
                            custom  : responses[3].data.results.custom,
                            image   : responses[3].data.results.image,
                        };
                    }
                }).catch((error) => {
                    this.handleError(error);
                });
            },
        },
    };
</script>

<style lang="scss" src="../scss/api-market.scss"></style>
